import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import type { CaseCardProps, CaseCardRef } from './CaseCard.data';
import styles from './CaseCard.module.scss';
import Text from '../../atoms/text/Text';
import Heading from '../../atoms/heading/Heading';
import { protocolRelativeToAbsoluteUrl } from 'util/url.utils';
import type { ThemeColorProviderContextType } from '../../unlisted/ThemeColorProvider';
import { useThemeColorProviderContext } from '../../unlisted/ThemeColorProvider';
import type { CardVideoRef } from './card-video/CardVideo.data';
import CardImage from './card-image/CardImage';
import { useGlobalStore } from '@/stores/global';
import type { HeadingRef } from '../../atoms/heading/Heading.data';
import type { TextRef } from '../../atoms/text/Text.data';
import Link from 'next/link';
import gsap from 'gsap';
import GrowingArrowButton from '../../atoms/growing-arrow-button/GrowingArrowButton';
import type { GrowingArrowButtonRef } from '../../atoms/growing-arrow-button/GrowingArrowButton.data';

const placeholderContent = {
  client: '',
  key_image: null,
  short_description: '',
  title: '',
  video: undefined,
};

const HEIGHT_SNAP_THRESHOLD_PX = 4;

export default function CaseCard({
  className,
  blok,
  imageInnerTransition,
  isPinnedToTop = false,
  ref,
}: CaseCardProps): ReactElement {
  const { fullBleed } = blok;
  const { content = placeholderContent } = blok.case;
  const globalStore = useGlobalStore();

  const localRef = useRef(null);

  const videoRef = useRef<CardVideoRef>(null);
  const elementRef = ((ref as RefObject<CaseCardRef | null>)?.current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  // const mediaRef = ((ref as RefObject<CaseCardRef | null>)?.current?.mediaRef ||
  //   localMediaRef) as RefObject<HTMLDivElement | null>;

  const cardImageRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HeadingRef>(null);
  const clientRef = useRef<TextRef>(null);
  const descriptionRef = useRef<TextRef>(null);
  const descriptionWrapperRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const buttonRef = useRef<GrowingArrowButtonRef>(null);
  const imageTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const textContentWrapperRef = useRef<HTMLDivElement>(null);
  const imageTimelineRafRef = useRef<number | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      mediaRef: cardImageRef,
      elementRef,
      cardVideoRef: videoRef,
    }),
    [cardImageRef, elementRef, videoRef],
  );

  const { isDarkTheme } = useThemeColorProviderContext() as ThemeColorProviderContextType;

  // short_description
  const { client, key_image, title, video, short_description } = content;
  let src: string | null = null;
  let srcDesktop: string | null = null;
  let altText: string | null = null;

  if (key_image?.[0]) {
    src = protocolRelativeToAbsoluteUrl(key_image[0].image_portrait);
    srcDesktop = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);
    altText = key_image[0].alt_text;
  } else if (video?.[0]?.poster_image?.filename) {
    src = protocolRelativeToAbsoluteUrl(video[0]?.poster_image.filename);
  }

  const onButtonTimelineCreate = useCallback(
    (timeline: gsap.core.Timeline) => {
      const textContentElement = textContentRef.current;

      if (!textContentElement) return;

      timeline.to(
        textContentElement,
        { opacity: fullBleed ? 1 : 0, ease: 'power2.out', duration: 0.3 },
        '<',
      );
    },
    [fullBleed],
  );

  useLayoutEffect(() => {
    const descriptionElement = descriptionRef.current;
    const descriptionWrapperElement = descriptionWrapperRef.current;
    const imageContainerElement = imageContainerRef.current;
    const textContentWrapperElement = textContentWrapperRef.current;
    const textContentElement = textContentRef.current;

    if (
      !descriptionElement ||
      !descriptionWrapperElement ||
      !imageContainerElement ||
      !textContentWrapperElement ||
      !textContentElement ||
      !elementRef.current
    )
      return;

    const cardElement = elementRef.current;

    const getElementHeight = (element: HTMLElement): number => {
      return Math.ceil(Math.max(element.getBoundingClientRect().height, element.scrollHeight));
    };

    const createImageTimeline = (): void => {
      gsap.set(descriptionWrapperElement, { clearProps: 'height' });
      gsap.set(cardElement, { clearProps: 'height' });
      const rawDescriptionHeight = getElementHeight(descriptionElement);
      const descriptionWidth = descriptionElement.getBoundingClientRect().width;

      gsap.set(textContentWrapperElement, { clearProps: 'maxHeight' });
      const rawTextContentHeight = getElementHeight(textContentElement);
      const shouldSnapHeights =
        Math.abs(rawDescriptionHeight - rawTextContentHeight) <= HEIGHT_SNAP_THRESHOLD_PX;
      const sharedHeight = Math.max(rawDescriptionHeight, rawTextContentHeight);
      const descriptionHeight = shouldSnapHeights ? sharedHeight : rawDescriptionHeight;
      const textContentHeight = shouldSnapHeights ? sharedHeight : rawTextContentHeight;

      if (descriptionHeight === textContentHeight) {
        // prevent "shaky-ness" of imageContainerElement
        gsap.set(imageContainerElement, {
          maxHeight: `${
            getElementHeight(imageContainerElement) +
            Math.min(rawDescriptionHeight, rawTextContentHeight) -
            Math.abs(rawDescriptionHeight - rawTextContentHeight)
          }px`,
        });
      }

      imageTimelineRef.current?.kill();

      imageTimelineRef.current = gsap
        .timeline({ paused: true })
        .fromTo(
          imageContainerElement,
          { width: '100%' },
          {
            width: fullBleed ? `calc(100% - ${descriptionWidth + 12}px)` : '100%',
            ease: 'power2.inOut',
            duration: 0.5,
          },
          '<',
        )
        .fromTo(
          descriptionWrapperElement,
          { height: 0 },
          { height: descriptionHeight, ease: 'power2.out', duration: 0.4 },
          '<',
        )
        .fromTo(
          textContentWrapperElement,
          { maxHeight: textContentHeight + 'px', opacity: 1 },
          {
            maxHeight: blok.fullBleed ? textContentHeight + 'px' : 0,
            ease: 'power2.out',
            duration: 0.4,
            opacity: blok.fullBleed ? 1 : 0,
          },
          blok.fullBleed ? '<20%' : '<',
        )
        .fromTo(descriptionElement, { opacity: 0 }, { opacity: 1, ease: 'power2.inOut' }, '<10%')
        .fromTo(
          descriptionWrapperElement,
          { overflow: 'hidden' },
          { overflow: 'visible', duration: 0 },
          '<',
        );
    };

    window.addEventListener('resize', createImageTimeline);

    const resizeObserver = new ResizeObserver(() => {
      createImageTimeline();
    });

    resizeObserver.observe(cardElement);
    resizeObserver.observe(descriptionElement);
    resizeObserver.observe(textContentElement);

    const fontSet = document.fonts;
    fontSet?.ready.then(() => {
      createImageTimeline();
    });

    return () => {
      window.removeEventListener('resize', createImageTimeline);
      resizeObserver.disconnect();
      if (imageTimelineRafRef.current) {
        cancelAnimationFrame(imageTimelineRafRef.current);
      }
      imageTimelineRef.current?.kill();
    };
  }, []);

  const onContainerMouseLeave = useCallback(() => {
    buttonRef.current?.reverse();
    imageTimelineRef.current?.reverse();
  }, []);

  const onContainerMouseEnter = useCallback(() => {
    buttonRef.current?.play();
    imageTimelineRef.current?.play();
  }, []);

  if (!blok.case || typeof blok.case === 'string') return <></>;

  const paddingFromTop = isPinnedToTop ? (elementRef.current?.getBoundingClientRect().top ?? 0) : 0;

  return (
    <Link ref={linkRef} href={blok.case.full_slug} className={classNames(styles.link, className)}>
      <div
        ref={elementRef}
        className={classNames(styles.card, {
          [styles.fullBleed]: fullBleed,
          [styles.darkTheme]: isDarkTheme,
          [styles.lightTheme]: !isDarkTheme,
        })}
        onMouseLeave={onContainerMouseLeave}
        onMouseEnter={onContainerMouseEnter}
        style={
          {
            '--padding-from-top': paddingFromTop,
          } as React.CSSProperties
        }
      >
        <div className={styles.imageContainer} ref={imageContainerRef}>
          <CardImage
            ref={cardImageRef}
            src={src}
            srcDesktop={srcDesktop}
            fullBleed={fullBleed}
            imageInnerTransition={imageInnerTransition}
            altText={altText}
            className={styles.cardImage}
          />
        </div>

        <div ref={descriptionWrapperRef} className={styles.descriptionWrapper}>
          <Text
            ref={descriptionRef}
            transitionInType="none"
            as="p"
            size="body-standard"
            className={classNames(styles.description)}
          >
            {short_description}
          </Text>
        </div>

        <div className={styles.projectInfo}>
          <div className={styles.textContentWrapper} ref={textContentWrapperRef}>
            <div className={styles.textContent} ref={textContentRef}>
              <Text
                ref={clientRef}
                transitionInType="none"
                as="p"
                size="body-standard"
                className={classNames(styles.client)}
              >
                {client}
              </Text>
              <Heading
                ref={titleRef}
                transitionInType="none"
                as="h4"
                size="label-large"
                className={styles.title}
              >
                {title}
              </Heading>
            </div>
          </div>

          <GrowingArrowButton
            ref={buttonRef}
            fullBleed={fullBleed}
            label={globalStore.globalSettings.view_case}
            onTimelineCreate={onButtonTimelineCreate}
            className={styles.growingArrowButton}
          />
        </div>
      </div>
    </Link>
  );
}
